import { createFileRoute, Link } from "@tanstack/react-router";
import { absoluteImageUrl } from "@/lib/schema-org";
import { THEMES } from "@/data/themes";
import { THEME_PAGES } from "@/data/theme-pages";
import { useReveal } from "@/hooks/use-reveal";
import { PageHero } from "@/components/page-hero";
import { metaDesc } from "@/lib/seo-description";

import { ReviewsProof } from "@/components/reviews-proof";
export const Route = createFileRoute("/soirees-thematiques/")({
  head: () => ({
    meta: [
      { title: "Soirées thématiques : fluo, mousse, CO₂ | DJ Val" },
      {
        name: "description",
        content:
          metaDesc("Soirées thématiques en Provence : canon CO₂, fluo, confettis, karaoké, bulles, mousse, neige artificielle. Animations DJ sur mesure dans tout le Vaucluse.")
      },
      { property: "og:title", content: "Soirées Thématiques | DJ Val" },
      { property: "og:description", content: metaDesc("Canon CO₂, fluo, karaoké, confettis, bulles, mousse, neige : 7 ambiances pour une soirée signature.") },
      { property: "og:image", content: absoluteImageUrl(THEMES[0].image) },
      { property: "og:url", content: "https://dj-val.fr/soirees-thematiques" },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/soirees-thematiques" }],
  }),
  component: Page,
});

function Page() {
  useReveal();
  return (
    <div className="bg-background text-muted-foreground">
      <PageHero
        image={THEMES[0].image}
        eyebrow="Animations signature · 7 univers"
        title={<>Soirées <span className="italic">thématiques</span></>}
        subtitle="CO₂, fluo, mousse, karaoké, neige, bulles, confettis. Sept univers à combiner pour transformer votre soirée en moment signature."
        primary={{ label: "Configurer ma soirée", to: "/contactez-nous" }}
        secondary={{ label: "07 67 90 86 67", href: "tel:0767908667" }}
        height="regular"
      />
      <ReviewsProof />

      <section className="py-20 px-6 max-w-7xl mx-auto">
        <div className="grid gap-12 md:grid-cols-2">
          {THEMES.map((t, i) => (
            <article
              key={t.slug}
              className="group sr border border-foreground/10 bg-card overflow-hidden"
              data-reveal
              style={{ transitionDelay: `${(i % 2) * 100}ms` }}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img loading="lazy" decoding="async" src={t.image} alt={t.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-8">
                <p className="eyebrow text-foreground">{t.punch}</p>
                <h2 className="font-serif-display text-3xl text-foreground mt-3">{t.name}</h2>
                <span className="gold-rule !w-8 my-4" />
                <p className="text-sm text-muted-foreground leading-relaxed font-light">{t.text}</p>
                {THEME_PAGES[t.slug] && (
                  <p className="mt-5 text-sm">
                    <Link
                      to="/soirees-thematiques/$slug"
                      params={{ slug: t.slug }}
                      className="text-[var(--gold)] underline underline-offset-4"
                    >
                      Tout savoir sur la {THEME_PAGES[t.slug].name.toLowerCase()} en Vaucluse
                    </Link>
                  </p>
                )}
                <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
                  <Link
                    to="/contactez-nous"
                    search={{ sujet: `Devis soirée ${t.name}` }}
                    className="inline-block bg-[var(--gold)] text-[var(--ink)] px-8 py-3 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all text-center"
                  >
                    Devis {t.name}
                  </Link>
                  <a
                    href="tel:0767908667"
                    className="text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/85 hover:text-foreground transition-colors underline underline-offset-4 decoration-foreground/30"
                  >
                    07 67 90 86 67
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="py-24 px-6 text-center border-t border-foreground/10">
        <div className="max-w-2xl mx-auto sr" data-reveal>
          <h2 className="font-serif-display text-4xl text-foreground">
            Combinez plusieurs effets <span className="italic text-foreground">pour un wow total</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <Link to="/contactez-nous" className="inline-block bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all">
            Configurer ma soirée
          </Link>
        </div>
      </section>
    </div>
  );
}
